import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { ApplicationModes } from '@shared/models/enums/enums';
import { RxAppId } from '@shared/models/consts';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { LoggerService } from '@core/services/logger/logger.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { Context, PendoSDK } from '@itero/pendo-sdk';
import { LdFeatureFlagsService } from '@shared/services/ld-feature-flags.service';

@Injectable({
	providedIn: 'root'
})
export class PendoService {
	private pendoSdk: PendoSDK;

	constructor(
		private readonly logger: LoggerService,
		private readonly shellQuery: ShellQuery,
		private readonly ldFeatureFlagsService: LdFeatureFlagsService
	) {}

	initPendo(): Observable<void> {
		return this.initPendoIfNeed();
	}

	/* istanbul ignore next */
	loadScript(src: string): Promise<PendoSDK> {
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');

			script.src = src;
			script.type = 'module';
			script.async = true;
			script.onload = () => {
				import(/* webpackIgnore: true */ src).then(module => resolve(module)).catch(error => reject(error));
			};
			script.onerror = () => {
				script?.remove();

				return reject(new Error(`Failed to load script: ${src}`));
			};
			document.head.appendChild(script);
		});
	}

	private initPendoIfNeed(): Observable<void> {
		return this.shellQuery.appSettings$.pipe(
			filter(appSettings => appSettings?.pendoSdkUrl?.length > 0),
			switchMap(appSettings => this.loadScript(appSettings.pendoSdkUrl)),
			tap(pendoSdk => (this.pendoSdk = pendoSdk)),
			switchMap(() => this.getApplicationModeIfSuitable()),
			switchMap(appMode => combineLatest([this.shellQuery.companySoftwareOptions$, this.ldFeatureFlagsService.getLDFeatureFlagsIfScanner(appMode)])),
			switchMap(([companySoftwareOptions, featureToggles]) => {
				const context: Context = {
					appName: RxAppId,
					companySoftwareOptions: [],
					companyId: this.shellQuery.companyId ?? 0,
					featureFlags: featureToggles,
					contactId: this.shellQuery.contactId ?? 0
				};

				return this.pendoSdk.initializePendoIfRequired(
					context,
					(message: string) => this.logger.error(message, { module: 'pendo-service' }),
					companySoftwareOptions ?? []
				);
			}),
			catchError(() => {
				this.logger.error('Pendo initialization error', { module: 'pendo-service' });

				return of();
			})
		);
	}

	private getApplicationModeIfSuitable(): Observable<ApplicationModes> {
		return this.shellQuery.applicationMode$.pipe(
			filter(appMode => !!appMode && !HostPlatformService.isOrthoCad(appMode as ApplicationModes)),
			map(appMode => appMode as ApplicationModes)
		);
	}
}
